import React, { useMemo } from 'react';
import { Typography, Checkbox, Box } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';
import { CdpResourceTableResetBar, useCdpResourceTable } from './cdpResourceTableSupport';

interface SyncedCondition {
  leadId?: string;
  conditionId?: number;
  value: string;
  codingSystem: string;
  synced: boolean;
}

interface ConditionsInCDPProps {
  selectedItems: Set<string>;
  onCheckboxChange: (itemId: string, itemType: string) => void;
  showSyncColumn?: boolean;
  syncedData?: SyncedCondition[];
  fieldConfigs?: Array<{ key: string; label: string }>;
  headerText?: string;
  syncText?: string;
}

const defaultColumns = [
  { key: 'conditionId', label: 'Code' },
  { key: 'value', label: 'Value' },
  { key: 'codingSystem', label: 'Coding System' },
];

const ConditionsInCDPSection: React.FC<ConditionsInCDPProps> = ({
  selectedItems,
  onCheckboxChange,
  showSyncColumn = true,
  syncedData,
  fieldConfigs,
  headerText,
  syncText,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  const configuredColumns = useMemo(() => {
    if (!fieldConfigs || !Array.isArray(fieldConfigs)) return defaultColumns;

    return fieldConfigs
      .map((config: any) => ({
        key: config?.fields?.ExpandedViewItemKey?.value || '',
        label: config?.fields?.ExpandedViewItemLabel?.value || '',
      }))
      .filter((item) => item.key && item.label);
  }, [fieldConfigs]);

  const baseColumns: DynamicColumn<SyncedCondition>[] = useMemo(() => {
    const leadIdColumn: DynamicColumn<SyncedCondition> = {
      id: 'leadId',
      label: 'Lead Id',
      accessor: 'leadId',
      render: (row) => (
        <Typography sx={{ fontSize: '12px' }}>{row.leadId?.trim() ? row.leadId : '-'}</Typography>
      ),
    };

    return [
      leadIdColumn,
      ...configuredColumns.map((config) => {
        return {
          id: config.key,
          label: config.label,
          accessor: config.key as keyof SyncedCondition,
          render: (row: SyncedCondition) => (
            <Typography sx={{ fontSize: '12px' }}>{(row as any)[config.key] || '-'}</Typography>
          ),
        };
      }),
      ...(showSyncColumn
        ? [
            {
              id: 'sync',
              label: syncText || 'Sync',
              accessor: 'synced' as keyof SyncedCondition,
              render: (row: SyncedCondition) => (
                <Box sx={{ textAlign: 'left' }}>
                  {row.synced ? (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '11px', color: '#4caf50', fontWeight: 500 }}
                    >
                      Synced
                    </Typography>
                  ) : (
                    <Checkbox
                      checked={selectedItems.has(`condition-${row.conditionId || ''}`)}
                      onChange={() =>
                        onCheckboxChange((row.conditionId || '').toString(), 'condition')
                      }
                      size="small"
                      sx={{
                        color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        '&.Mui-checked': {
                          color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        },
                      }}
                    />
                  )}
                </Box>
              ),
            } as DynamicColumn<SyncedCondition>,
          ]
        : []),
    ];
  }, [configuredColumns, showSyncColumn, selectedItems, onCheckboxChange, syncText]);

  const { columns, tableData, hasActiveFilterOrSort, onResetFilters } = useCdpResourceTable(
    baseColumns,
    syncedData
  );

  if (!syncedData || syncedData.length === 0) return null;

  return (
    <CustomAccordion
      title={`${headerText || 'Conditions in CDP'} (${syncedData.length})`}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1.5,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
          backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
          color: 'white',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
          color: 'white !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
          p: 0,
          borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
          border: '2px solid',
          borderTop: 'none',
        },
      }}
      summarySx={{
        backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
        color: 'white',
        '& .MuiTypography-root': {
          color: 'white',
          fontSize: '15px',
          fontWeight: 400,
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
      detailsSx={{
        p: 0,
        borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
        border: '2px solid',
        borderTop: 'none',
      }}
    >
      <CdpResourceTableResetBar hasActive={hasActiveFilterOrSort} onReset={onResetFilters} />
      <DynamicTable
        columns={columns}
        data={tableData}
        variant="compact"
        noDataMessage="No conditions found"
        getRowId={(row) =>
          `${row.leadId ?? ''}-${row.conditionId ?? ''}-${row.value}-${row.codingSystem}`
        }
      />
    </CustomAccordion>
  );
};

export default ConditionsInCDPSection;
